import React from 'react';

import { useEventCallback, useOnPageBecomeActive } from '@gitbook/util-react-hooks';
import { ConnectionPool } from '@gitbook/util-websocket';

import {
    DISCONNECTED_AGENT_STATE,
    GitBookAgentClient,
    type GitBookAgentClientOptions,
    type GitBookAgentClientState,
    GitBookAgentClientStatus,
} from './GitBookAgentClient';

type AgentPool = ConnectionPool<GitBookAgentClient>;

const AgentContext = React.createContext<AgentPool | null>(null);

export function AgentProvider({ children }: { children: React.ReactNode }) {
    const pool = React.useMemo(
        () =>
            new ConnectionPool<GitBookAgentClient>({
                onDestroy: (agent) => {
                    agent.disconnect();
                },
                ttl: 1000 * 60 * 5,
            }),
        []
    );

    return <AgentContext.Provider value={pool}>{children}</AgentContext.Provider>;
}

/**
 * Return a function to imperatively create a new agent client.
 */
export function useGetAgent() {
    const pool = React.useContext(AgentContext);
    if (!pool) {
        throw new Error('useGetAgent should be used within an AgentProvider');
    }

    return React.useCallback(
        <Rpc extends {} = {}>(options: GitBookAgentClientOptions): GitBookAgentClient<Rpc> => {
            // @ts-expect-error - ConnectionPool is not generic
            const poolAs = pool as ConnectionPool<GitBookClient<Rpc>>;
            const agent = poolAs.get(options.pathname, () => {
                const newAgent = new GitBookAgentClient<Rpc>(options);
                newAgent.connect();

                return newAgent;
            });

            // The conversation has one connection, whoever asks for it: a caller that needs the
            // transcript promotes the client a watcher opened, rather than opening a second socket
            // beside it and leaving the first to be collected with whatever it still held.
            if (options.transcript !== false) {
                agent.ensureTranscript();
            }

            return agent;
        },
        [pool]
    );
}

/**
 * Return a function that drops a pooled agent by its pathname, disconnecting it.
 *
 * For a conversation that no longer exists. A pooled client outlives the component that used it, and
 * reads the socket closing — which is what destroying its durable object does — as a connection to
 * re-establish. Left in the pool it reconnects, re-provisions, and can deliver whatever was still in
 * its outbox into a conversation the user has deleted.
 */
export function useDropAgent() {
    const pool = React.useContext(AgentContext);
    if (!pool) {
        throw new Error('useDropAgent should be used within an AgentProvider');
    }

    return React.useCallback(
        (pathname: string) => {
            const agent = pool.get(pathname);
            if (agent) {
                pool.delete(agent);
            }
        },
        [pool]
    );
}

/**
 * Hook to connect to a content agent.
 *
 * Pass `enabled: false` to skip connecting entirely (no socket is opened and `null` is returned),
 * for cases where the agent is only needed conditionally. Pass `transcript: false` to open the
 * connection as a watcher — see {@link GitBookAgentClientOptions.transcript}, which also covers
 * what happens when a caller that needs the conversation reaches the same client.
 */
export function useAgent<Rpc extends {} = {}>(
    options: GitBookAgentClientOptions & { enabled: boolean }
): GitBookAgentClient<Rpc> | null;
export function useAgent<Rpc extends {} = {}>(
    options: GitBookAgentClientOptions
): GitBookAgentClient<Rpc>;
export function useAgent<Rpc extends {} = {}>(
    options: GitBookAgentClientOptions & { enabled?: boolean }
): GitBookAgentClient<Rpc> | null {
    const { enabled = true } = options;
    const pool = React.useContext(AgentContext);
    if (!pool) {
        throw new Error('useAgent should be used within an AgentProvider');
    }
    const getAgent = useGetAgent();
    const getToken = useEventCallback(options.getToken);
    const prepareConnection = useEventCallback(async (connection: { transcript: boolean }) =>
        options.prepareConnection?.(connection)
    );

    const agent = React.useMemo(() => {
        if (!enabled) {
            return null;
        }
        return getAgent<Rpc>({
            getToken,
            prepareConnection,
            pathname: options.pathname,
            transcript: options.transcript,
        });
    }, [enabled, getAgent, options.pathname, options.transcript, getToken, prepareConnection]);

    React.useEffect(() => {
        if (!agent) {
            return;
        }
        // Cast through a shallow signature: GitBookAgentClient's RPC-generic type is
        // deep enough to trip TypeScript 7's instantiation-depth limit (TS2589) here.
        return (pool as { keepAlive: (agent: unknown) => () => void }).keepAlive(agent);
    }, [pool, agent]);

    // Recover quickly when the user comes back to the page: the transport reconnects
    // on its own with an exponential backoff (up to 30s), but after the page has been
    // hidden for a while (computer asleep, tab backgrounded) we might be deep into
    // that backoff, leaving the user on a DISCONNECTED state for too long.
    useOnPageBecomeActive(agent ? () => agent.reconnect() : undefined);

    return agent;
}

/**
 * What {@link useAgentState} reads of an agent.
 *
 * Because of https://github.com/cloudflare/capnweb/issues/53 we need to use a very basic type to
 * avoid type errors. Named so a caller holding more than one kind of client can narrow to it before
 * calling: the checker resolves each client's RPC type against this shape separately rather than
 * resolving a union of them at once, which is deep enough to exhaust its instantiation budget.
 */
export type AgentStateSource = {
    listen: (event: 'state', callback: (state: GitBookAgentClientState) => void) => () => void;
    state: GitBookAgentClientState;
} | null;

/** The edit target the agent reports, for {@link useAgentState}. */
export function selectAgentEditTarget(state: GitBookAgentClientState) {
    return state.editTarget;
}

/**
 * Hook to get the state of the agent.
 */
export function useAgentState<
    MapState extends undefined | ((state: GitBookAgentClientState) => any) = undefined,
>(
    agent: AgentStateSource,
    mapState?: MapState
): MapState extends (state: GitBookAgentClientState) => any
    ? ReturnType<MapState>
    : GitBookAgentClientState {
    return React.useSyncExternalStore(
        React.useCallback(
            (callback) => {
                // No agent (e.g. a disabled connection): nothing to subscribe to.
                return agent ? agent.listen('state', callback) : () => {};
            },
            [agent]
        ),
        React.useCallback(() => {
            const state = agent ? agent.state : DISCONNECTED_AGENT_STATE;
            return mapState ? mapState(state) : state;
        }, [agent, mapState])
    );
}

/**
 * Call `onTurnEnd` each time the agent finishes a turn, for refreshing API data it may have changed.
 *
 * A turn ends when the agent stops processing, even if the connection dropped in between: the
 * agent drops the socket routinely, so its end often arrives through a reconnection.
 */
export function useOnAgentTurnEnd(agent: AgentStateSource, onTurnEnd: () => void): void {
    const callback = useEventCallback(onTurnEnd);

    React.useEffect(() => {
        if (!agent) {
            return;
        }

        let working = agent.state.status === GitBookAgentClientStatus.PROCESSING;
        return agent.listen('state', (state) => {
            if (state.status === GitBookAgentClientStatus.PROCESSING) {
                working = true;
            } else if (
                working &&
                (state.status === GitBookAgentClientStatus.IDLE ||
                    state.status === GitBookAgentClientStatus.ERRORED)
            ) {
                working = false;
                callback();
            }
        });
    }, [agent, callback]);
}

/**
 * Helper extractor to check if an agent has some messages in its state.
 */
export function agentHasMessages(state: GitBookAgentClientState) {
    return Boolean(state.messages?.length);
}

/**
 * Helper extractor to check whether the agent has sent what the conversation holds.
 *
 * Until it has, {@link agentHasMessages} answers false because nothing has arrived yet — which is
 * not the same as the conversation being empty, and reading it that way shows a started
 * conversation whatever an empty one is offered.
 */
export function agentIsSynced(state: GitBookAgentClientState) {
    return state.synced;
}

/**
 * Helper extractor to check if an agent is currently processing (i.e. has a non-empty pendingMessage in its state).
 */
export function agentIsProcessing(state: GitBookAgentClientState) {
    return state.status === GitBookAgentClientStatus.PROCESSING;
}

/**
 * Helper extractor to check if an agent is waiting for the user to answer a question.
 */
export function agentHasPendingQuestion(state: GitBookAgentClientState) {
    return state.pendingQuestion !== null;
}

/**
 * Helper extractor to check if an agent is currently idle (i.e. is not loading and has no pending messages in its state).
 */
export function agentIsIdle(state: GitBookAgentClientState) {
    return state.status === GitBookAgentClientStatus.IDLE;
}
